import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getPlatformStats,
  getAllCompanies,
  clearError,
} from "../../store/slices/superAdminCompanySlice";
import { Building2, Users, Shield, Eye, Plus } from "lucide-react";
import Button from "../../components/UI/Button";
import { toast } from "react-toastify";
import { ROOT_DOMAIN } from "../../utils/getRootDomain";

const SuperAdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { platformStats, companies, loading, error } = useAppSelector(
    (state: any) => state.superAdminCompanies,
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getPlatformStats()).unwrap();
        await dispatch(
          getAllCompanies({
            page: 1,
            limit: 5,
            sortBy: "createdAt",
            sortOrder: "desc",
          }),
        ).unwrap();
      } catch (err: any) {
        toast.error(err || "Failed to load dashboard data");
      }
    };

    loadData();
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  if (loading && !platformStats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const recentCompanies = companies.slice(0, 5);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Platform Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your platform statistics
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Companies Card */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Companies
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {platformStats?.companies.total || 0}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 border-t border-gray-200">
            <div className="text-sm flex items-center justify-between">
              <div>
                <span className="text-green-600 font-medium">
                  {platformStats?.companies.active || 0} Active
                </span>
                <span className="text-gray-400 mx-2">•</span>
                <span className="text-red-600 font-medium">
                  {platformStats?.companies.inactive || 0} Inactive
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Users Card */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Users
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {platformStats?.users.total || 0}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 border-t border-gray-200">
            <div className="text-sm">
              <span className="text-green-600 font-medium">
                {platformStats?.users.active || 0} Active
              </span>
            </div>
          </div>
        </div>

        {/* SuperAdmins Card */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
          <div className="p-5">
            <div className="flex items-center">
              <div className="shrink-0">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    SuperAdmins
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {platformStats?.superAdmins || 0}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 border-t border-gray-200">
            <div className="text-sm text-gray-500">Platform administrators</div>
          </div>
        </div>
      </div>

      {/* Recent Companies */}
      {recentCompanies.length > 0 && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Recent Companies
              </h2>
              <Link to="/companies">
                <Button variant="outline" size="sm">
                  View All
                </Button>
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentCompanies.map((company: any) => (
              <Link
                key={company._id}
                to={`/companies/${company._id}`}
                className="block hover:bg-gray-50 transition-colors"
              >
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="shrink-0">
                        <Building2 className="h-5 w-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {company.name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {company.subdomain}.{ROOT_DOMAIN}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      {company.stats && (
                        <span className="text-sm text-gray-500">
                          {company.stats.totalUsers} users
                        </span>
                      )}
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          company.status === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {company.status}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Link to="/companies">
            <Button variant="outline" className="w-full justify-center">
              <Eye className="h-4 w-4 mr-2" />
              View All Companies
            </Button>
          </Link>
          <Link to="/companies/create">
            <Button variant="primary" className="w-full justify-center">
              <Plus className="h-4 w-4 mr-2" />
              Create New Company
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
